import React, { useState, useEffect, useCallback } from "react"
import { Box, Typography, Chip, List, ListItemButton, ListItemText, ListItemIcon, CircularProgress } from "@mui/material"
import EventIcon from '@mui/icons-material/Event'
import BlockIcon from '@mui/icons-material/Block'
import { GetAvailableSlots } from "../../../api"

const formatTime12 = (t) => {
    const [h, m] = t.split(':').map(Number)
    const period = h >= 12 ? 'pm' : 'am'
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
    return m === 0 ? `${h12}${period}` : `${h12}:${String(m).padStart(2, '0')}${period}`
}

const SlotPicker = ({ orgId, date, selectedSlot, onSelect, filterChildOrg, isParent }) => {
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)

    const fetchSlots = useCallback(() => {
        if (!orgId || !date) { setData(null); return }
        setLoading(true)
        GetAvailableSlots(orgId, date)
            .then(r => setData(r))
            .catch(() => setData(null))
            .finally(() => setLoading(false))
    }, [orgId, date])

    useEffect(() => { fetchSlots() }, [fetchSlots])

    if (loading) return <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>
    if (!data || !data.slots?.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No slots available for this date. Click Consult to proceed with scheduling.</Typography>

    const dayFull = data.dayMax != null && data.dayTotalCases >= data.dayMax
    const slots = filterChildOrg ? data.slots.filter(s => {
        const id = String(s.childOrg?.id || s.childOrg?._id || s.childOrg)
        return id === filterChildOrg
    }) : data.slots

    if (!slots.length) return <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>No slots available for your clinic on this date. Click Consult to proceed with scheduling.</Typography>

    return (
        <Box>
            {data.dayMax != null && (
                <Chip size="small" sx={{ mb: 1 }}
                    label={`Day: ${data.dayTotalCases}/${data.dayMax} cases`}
                    color={dayFull ? 'error' : 'default'} />
            )}
            <List dense disablePadding>
                {slots.map(slot => {
                    const slotFull = slot.maxCases != null && slot.caseCount >= slot.maxCases
                    const disabled = dayFull || slotFull
                    const selected = selectedSlot?.slotId === slot.slotId
                    const orgName = isParent ? (slot.childOrg?.name || 'Unknown') : (slot.orgName || 'Unknown')
                    const capacityLabel = slot.maxCases != null ? `${slot.caseCount}/${slot.maxCases}` : `${slot.caseCount}/∞`

                    return (
                        <ListItemButton key={slot.slotId} selected={selected} disabled={disabled}
                            onClick={() => onSelect(selected ? null : slot)}
                            sx={{ borderLeft: 3, borderColor: slot.color || '#1976d2', borderRadius: 1, mb: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                                {disabled ? <BlockIcon fontSize="small" color="error" /> : <EventIcon fontSize="small" />}
                            </ListItemIcon>
                            <ListItemText
                                primary={`${orgName} — ${formatTime12(slot.startTime)}–${formatTime12(slot.endTime)}`}
                                secondary={`${capacityLabel} cases${slot.notes ? ` · ${slot.notes}` : ''}`}
                            />
                            {disabled && <Chip size="small" label="Full" color="error" sx={{ ml: 1 }} />}
                        </ListItemButton>
                    )
                })}
            </List>
        </Box>
    )
}

export default SlotPicker
